import { notEmpty } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { ContentHeadlineVariant } from "~/components/content/content-headline";
import { MagazinesStyleTagsMap } from "~/components/content/content-headline/content-headline-tag/variants/magazines-style/const";
import { article as articleData } from "~/data";
import type { contentHeadlineTagContent$key } from "~/queries/__generated__/contentHeadlineTagContent.graphql";

import { useDetermineHeadlineTag } from "./hooks";
import { Description, Flag, FlagDivider, Heading, StyledTooltip } from "./styles";

export type Props = {
  hideTooltip?: boolean;
  reference: contentHeadlineTagContent$key;
  useSectionTags?: boolean;
  variant?: ContentHeadlineVariant;
};

export const ContentHeadlineTag: FunctionComponent<Props> = ({
  hideTooltip,
  reference: reference_,
  useSectionTags = false,
  variant,
}) => {
  const content = useFragment(
    graphql`
      fragment contentHeadlineTagContent on Content {
        ...hooksContentHeadlineTagContent
      }
    `,
    reference_,
  );
  const headlineTag = useDetermineHeadlineTag(content, useSectionTags);
  if (!headlineTag || !headlineTag.name) return null;

  const handleRenderFlag = () => {
    const handleRenderMagazinesStyleTags = () => {
      const matchedArticleType = Object.values(articleData.types).find(
        (type: { entityId: string; name?: string }) => {
          if (!type.name || !headlineTag.name) return;
          return type.name.toLowerCase() === headlineTag.name.toLowerCase();
        },
      );
      const articleTypeEntityId = headlineTag.entityId ?? matchedArticleType?.entityId ?? null;

      if (!articleTypeEntityId || variant !== "magazines-style") return null;
      return MagazinesStyleTagsMap[articleTypeEntityId];
    };

    const divider =
      variant === "magazines-style" ? (
        <FlagDivider> / </FlagDivider>
      ) : (
        <FlagDivider> | </FlagDivider>
      );

    return (
      <Flag $variant={variant} color={headlineTag.color}>
        {handleRenderMagazinesStyleTags() ?? headlineTag.name}
        {divider}
      </Flag>
    );
  };
  if (notEmpty(hideTooltip) || !headlineTag.description) return handleRenderFlag();

  return (
    <StyledTooltip
      placement="bottom-start"
      title={
        <>
          {headlineTag.name && <Heading>{headlineTag.name}</Heading>}
          {headlineTag.description && <Description>{headlineTag.description}</Description>}
        </>
      }
      arrow
    >
      {handleRenderFlag()}
    </StyledTooltip>
  );
};

ContentHeadlineTag.displayName = "ContentHeadlineTag";
